// eslint-disable-next-line @typescript-eslint/no-unsafe-function-type
export type NonObject = undefined | null | boolean | string | number | Function | Date;
type NonConstructorKeys<T> = {
    [P in keyof T]: T[P] extends new () => unknown ? never : P;
}[keyof T];
type NonConstructor<T> = Pick<T, NonConstructorKeys<T>>;

/**
 * Removes non-data fns from an SDK DTO type
 */
export type NonSdkClass<T> = NonConstructor<Omit<T, 'fromJS' | 'init' | 'toJSON'>>;

/**
 * Removes non-data fns from an SDK DTO type and all its child props
 */
export type RecurseNonSdkClass<T> = T extends NonObject
    ? T
    : T extends Array<infer U>
      ? Array<RecurseNonSdkClass<U>>
      : T extends ReadonlyArray<infer U>
        ? ReadonlyArray<RecurseNonSdkClass<U>>
        : NonSdkClass<{ [K in keyof T]: RecurseNonSdkClass<T[K]> }>;

export function toSdkClass<T>(
    type: { fromJS: (data: unknown) => T },
    data: NoInfer<RecurseNonSdkClass<T>>,
): T {
    return type.fromJS(data);
}

/**
 * A plain-data copy of SDK data: SDK class instances, at any depth, become
 * plain objects without `fromJS` / `init` / `toJSON`. `Date`s stay `Date`s.
 *
 * In tests, `timezone-mock` replaces the global `Date` with one that
 * `structuredClone` doesn't recognize, so copied `Date`s become plain objects
 * there. Tests that need copied `Date`s must unregister `timezone-mock`.
 */
export function toPlainData<T>(value: T): RecurseNonSdkClass<T> {
    // SDK classes keep their data in own properties and their methods on the
    // prototype, which structuredClone drops
    return structuredClone(value) as RecurseNonSdkClass<T>;
}
